<template>
  <div style="min-height: 450px; height: 450px;">
    <div v-if="loading">
      <loading-container />
    </div>
    <app-echart-scatter
      v-else
      ref="chart"
      :key="chartKey"
      :option-data="chartData"
      style="height: 100%; width: 100%;"
    />
  </div>
</template>

<script>
import AppEchartScatter from '@core/components/charts/echart/AppEchartScatter.vue'
import useAppConfig from '@core/app-config/useAppConfig'
import LoadingContainer from '@/views/components/LoadingContainer.vue'
import renderChartToBase64 from './offscreenRender'

export default {
  components: {
    AppEchartScatter,
    LoadingContainer,
  },
  props: {
    payoffChartData: {
      type: Object,
      default: () => ({
        SHOWDATA: [],
      }),
    },
  },
  setup() {
    const { skin } = useAppConfig()
    return { skin }
  },
  data() {
    return {
      loading: true,
      chartKey: 0,
      chartData: {
        legend: {},
        grid: {},
        xAxis: {},
        yAxis: {},
        series: [],
      },
      cachedImage: null,
    }
  },
  computed: {
    splitLineOpacity() {
      return this.skin === 'dark' ? 0.1 : 1
    },
  },
  watch: {
    payoffChartData: {
      handler(newData) {
        if (newData && newData.SHOWDATA) {
          if (Array.isArray(newData.SHOWDATA) && newData.SHOWDATA.length > 0) {
            this.processPayoffData(newData)
          } else {
            this.loading = false
          }
        } else {
          this.loading = false
        }
      },
      immediate: true,
      deep: true,
    },
    skin() {
      // Re-process data when theme changes
      if (this.payoffChartData && this.payoffChartData.SHOWDATA && this.payoffChartData.SHOWDATA.length > 0) {
        this.processPayoffData(this.payoffChartData)
        this.chartKey += 1
      }
    },
  },
  mounted() {
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeChart)
  },
  methods: {
    processPayoffData(payoffData) {
      if (!payoffData.SHOWDATA || payoffData.SHOWDATA.length === 0) {
        this.loading = false
        return
      }

      // Process series data
      const series = payoffData.SHOWDATA.map((seriesItem, index) => {
        let data = []

        if (seriesItem.data && seriesItem.data.length > 0) {
          // Handle both array [x, y] and object {x, y} formats
          data = seriesItem.data.map(point => {
            if (Array.isArray(point)) {
              return [parseFloat(point[0]), parseFloat(point[1])]
            }
            if (point.x !== undefined && point.y !== undefined) {
              return [parseFloat(point.x), parseFloat(point.y)]
            }
            return [0, 0]
          })
        }

        return {
          name: seriesItem.name || `Series ${index + 1}`,
          type: 'scatter',
          data,
          symbolSize: index === 0 ? 6 : 8,
          itemStyle: {
            color: index === 0 ? 'lightblue' : '#ff6b6b',
          },
        }
      })

      this.chartData = {
        legend: {
          show: series.length > 1,
          top: 10,
          left: 'center',
        },
        grid: {
          left: 60,
          right: 20,
          bottom: 60,
          top: series.length > 1 ? 50 : 20,
          containLabel: true,
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            snap: false,
            crossStyle: {
              color: '#999',
            },
          },
          formatter: params => {
            if (params && params.length > 0) {
              const pointData = params[0].data
              const underlying = Number(pointData[0]).toFixed(2)
              const payoff = Number(pointData[1]).toFixed(2)
              return `Underlying: ${underlying}%<br/>Payoff: ${payoff}%`
            }
            return ''
          },
        },
        xAxis: {
          type: 'value',
          name: 'Underlying',
          nameLocation: 'middle',
          nameGap: 35,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 12,
          },
          scale: true,
          splitLine: {
            show: false,
          },
        },
        yAxis: {
          type: 'value',
          name: 'Payoff',
          nameLocation: 'middle',
          nameGap: 50,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 12,
          },
          scale: true,
          splitLine: {
            lineStyle: {
              opacity: this.splitLineOpacity,
              type: 'dashed',
            },
          },
        },
        series,
      }
      this.cachedImage = renderChartToBase64(this.chartData)
      this.loading = false
    },
    resizeChart() {
      if (this.$refs.chart) {
        this.$refs.chart.resize()
      }
    },
    getDataURL() {
      return this.cachedImage || null
    },
  },
}
</script>
